import { useRef, useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { useQuery } from "@tanstack/react-query";
import { toast } from "sonner";
import { XIcon, DownloadSimpleIcon, CopyIcon, XLogoIcon, TelegramLogoIcon, SpinnerGapIcon } from "@phosphor-icons/react";
import { fetchIndicators, fetchRugcheck, trackEvent } from "../lib/api";
import { getClientId } from "../lib/clientId";
import { stampBrandApe } from "../lib/shareLogo";

const NEON = "#00E599", DANGER = "#FF3366", WARN = "#F5A623", MUTED = "#8b8b8b";

const fmtP = (n) => {
  if (n == null) return "—";
  if (n >= 1) return `$${n.toFixed(4)}`;
  if (n >= 0.0001) return `$${n.toFixed(6)}`;
  return `$${n.toFixed(10).replace(/0+$/, "")}`;
};
const verdictColor = (v) => (v === "High Confidence" ? NEON : v === "Moderate Confidence" ? NEON : v === "Elevated Risk" ? WARN : v === "High Risk" ? DANGER : "#8A8F98");

export const CoinShareCard = ({ coin, open, onClose }) => {
  const canvasRef = useRef(null);
  const [copying, setCopying] = useState(false);
  const indQ = useQuery({ queryKey: ["indicators", coin.address, "1h"], queryFn: () => fetchIndicators(coin.address, "1h"), enabled: open });
  const rugQ = useQuery({ queryKey: ["rugcheck", coin.address], queryFn: () => fetchRugcheck(coin.address), enabled: open });

  const shareUrl = `https://alphaapeing.com/coin/${coin.address}`;
  const shareText = `$${coin.symbol} — Degen Score ${coin.score.total}/100 (${coin.score.verdict}) on AlphaApeing 🦍`;

  useEffect(() => {
    if (!open || !canvasRef.current) return;
    const c = canvasRef.current;
    const ctx = c.getContext("2d");
    const W = 1080, H = 1080;
    // bg
    ctx.fillStyle = "#050505";
    ctx.fillRect(0, 0, W, H);
    // faint grid
    ctx.strokeStyle = "rgba(255,255,255,0.035)";
    ctx.lineWidth = 1;
    for (let x = 0; x <= W; x += 60) { ctx.beginPath(); ctx.moveTo(x, 0); ctx.lineTo(x, H); ctx.stroke(); }
    for (let y = 0; y <= H; y += 60) { ctx.beginPath(); ctx.moveTo(0, y); ctx.lineTo(W, y); ctx.stroke(); }
    // border
    ctx.strokeStyle = "rgba(0,229,153,0.30)";
    ctx.lineWidth = 4;
    ctx.strokeRect(24, 24, W - 48, H - 48);

    // header wordmark
    ctx.textBaseline = "alphabetic";
    ctx.font = "800 46px Arial, sans-serif";
    ctx.fillStyle = "#fff";
    ctx.fillText("ALPHA", 70, 120);
    const w1 = ctx.measureText("ALPHA").width;
    ctx.fillStyle = NEON;
    ctx.fillText("APEING", 70 + w1, 120);
    ctx.font = "600 20px monospace";
    ctx.fillStyle = NEON;
    ctx.fillText("SOLANA MEME-COIN INTELLIGENCE", 72, 152);

    // monogram
    const cx = 200, cy = 300;
    ctx.beginPath();
    ctx.arc(cx, cy, 74, 0, Math.PI * 2);
    ctx.fillStyle = "rgba(0,229,153,0.12)";
    ctx.fill();
    ctx.lineWidth = 3;
    ctx.strokeStyle = NEON;
    ctx.stroke();
    ctx.fillStyle = NEON;
    ctx.font = "800 46px Arial, sans-serif";
    ctx.textAlign = "center";
    ctx.fillText((coin.symbol || "?").replace("$", "").slice(0, 3).toUpperCase(), cx, cy + 16);
    ctx.textAlign = "left";

    // symbol + name
    ctx.fillStyle = "#fff";
    ctx.font = "800 68px Arial, sans-serif";
    ctx.fillText(`$${(coin.symbol || "").replace("$", "")}`, 300, 285);
    ctx.fillStyle = MUTED;
    ctx.font = "400 30px Arial, sans-serif";
    ctx.fillText((coin.name || "").slice(0, 28), 302, 330);

    // Degen score big
    ctx.fillStyle = MUTED;
    ctx.font = "600 24px monospace";
    ctx.fillText("DEGEN SCORE", 70, 470);
    ctx.fillStyle = verdictColor(coin.score.verdict);
    ctx.font = "800 200px Arial, sans-serif";
    ctx.fillText(String(coin.score.total), 66, 640);
    const sw = ctx.measureText(String(coin.score.total)).width;
    ctx.font = "700 40px Arial, sans-serif";
    ctx.fillStyle = MUTED;
    ctx.fillText("/100", 66 + sw + 14, 640);
    ctx.fillStyle = verdictColor(coin.score.verdict);
    ctx.font = "700 34px monospace";
    ctx.fillText(coin.score.verdict, 70, 690);

    // stat cards row
    const ch24 = coin.priceChange?.h24 ?? 0;
    const rsi = indQ.data?.available ? indQ.data.rsi : null;
    const stats = [
      { label: "PRICE", value: fmtP(coin.priceUsd), color: "#fff" },
      { label: "24H", value: `${ch24 >= 0 ? "+" : ""}${ch24.toFixed(1)}%`, color: ch24 >= 0 ? NEON : DANGER },
      { label: "RSI (1h)", value: rsi != null ? String(rsi) : "—", color: rsi == null ? MUTED : rsi >= 70 ? DANGER : rsi <= 30 ? NEON : WARN },
    ];
    const bx = 70, by = 740, bw = 300, bh = 130, gap = 20;
    stats.forEach((s, i) => {
      const x = bx + i * (bw + gap);
      ctx.strokeStyle = "rgba(255,255,255,0.12)";
      ctx.lineWidth = 2;
      ctx.strokeRect(x, by, bw, bh);
      ctx.fillStyle = MUTED;
      ctx.font = "600 22px monospace";
      ctx.fillText(s.label, x + 24, by + 46);
      ctx.fillStyle = s.color;
      ctx.font = "800 44px Arial, sans-serif";
      ctx.fillText(s.value, x + 24, by + 100);
    });

    // rug verdict row
    const rug = rugQ.data?.available ? rugQ.data : null;
    ctx.font = "600 26px monospace";
    let ry = 940;
    if (rug) {
      const chips = [
        { t: rug.mintRevoked ? "✓ Mint revoked" : "✕ Mint active", ok: rug.mintRevoked },
        { t: rug.freezeRevoked ? "✓ Freeze revoked" : "✕ Freeze active", ok: rug.freezeRevoked },
        { t: `Top10 ${rug.top10Pct}%`, ok: rug.top10Pct < 50 },
      ];
      let rx = 70;
      chips.forEach((ch) => {
        ctx.fillStyle = ch.ok ? NEON : DANGER;
        ctx.fillText(ch.t, rx, ry);
        rx += ctx.measureText(ch.t).width + 40;
      });
    } else {
      ctx.fillStyle = MUTED;
      ctx.fillText("Rug scan: run on alphaapeing.com", 70, ry);
    }

    // footer
    ctx.fillStyle = NEON;
    ctx.font = "700 30px monospace";
    ctx.fillText("alphaapeing.com", 70, 1015);
    ctx.fillStyle = MUTED;
    ctx.font = "400 22px Arial, sans-serif";
    ctx.textAlign = "right";
    ctx.fillText("Research tool · not financial advice", W - 70, 1015);
    ctx.textAlign = "left";
    stampBrandApe(c);
  }, [open, coin, indQ.data, rugQ.data]);

  if (!open) return null;

  const getBlob = () => new Promise((res) => canvasRef.current.toBlob(res, "image/png"));

  const download = async () => {
    const blob = await getBlob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `alphaapeing-${coin.symbol}.png`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success("Flex card downloaded");
  };

  const copy = async () => {
    setCopying(true);
    try {
      const blob = await getBlob();
      await navigator.clipboard.write([new window.ClipboardItem({ "image/png": blob })]);
      toast.success("Image copied — paste it into your post");
    } catch {
      toast.error("Copy not supported here — use Download instead");
    }
    setCopying(false);
  };

  const shareX = () => { trackEvent("share", getClientId(), { mint: coin.address, via: "x" }); window.open(`https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}&url=${encodeURIComponent(shareUrl)}`, "_blank"); };
  const shareTg = () => { trackEvent("share", getClientId(), { mint: coin.address, via: "telegram" }); window.open(`https://t.me/share/url?url=${encodeURIComponent(shareUrl)}&text=${encodeURIComponent(shareText)}`, "_blank"); };

  return createPortal(
    <div data-testid="share-card-modal" className="fixed inset-0 z-[100] flex items-center justify-center bg-black/80 backdrop-blur-sm p-4" onClick={onClose}>
      <div className="bg-card border border-white/15 max-w-md w-full p-5 space-y-4 max-h-[92vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between">
          <h3 className="font-heading font-bold text-lg">Share {coin.symbol} flex card</h3>
          <button data-testid="share-close-btn" onClick={onClose} className="p-1.5 text-muted-foreground hover:text-white"><XIcon size={20} /></button>
        </div>

        <div className="border border-white/10 overflow-hidden">
          <canvas ref={canvasRef} width={1080} height={1080} className="w-full h-auto block" />
        </div>

        <div className="grid grid-cols-2 gap-2">
          <button data-testid="share-download-btn" onClick={download} className="flex items-center justify-center gap-2 px-4 py-2.5 border border-white/15 text-sm font-semibold hover:border-neon/40 hover:text-neon transition-colors">
            <DownloadSimpleIcon size={16} /> Download
          </button>
          <button data-testid="share-copy-btn" onClick={copy} disabled={copying} className="flex items-center justify-center gap-2 px-4 py-2.5 border border-white/15 text-sm font-semibold hover:border-neon/40 hover:text-neon transition-colors disabled:opacity-50">
            {copying ? <SpinnerGapIcon size={16} className="animate-spin" /> : <CopyIcon size={16} />} Copy image
          </button>
          <button data-testid="share-x-btn" onClick={shareX} className="flex items-center justify-center gap-2 px-4 py-2.5 bg-white text-black text-sm font-semibold hover:bg-white/90 transition-colors">
            <XLogoIcon size={16} weight="fill" /> Share on X
          </button>
          <button data-testid="share-tg-btn" onClick={shareTg} className="flex items-center justify-center gap-2 px-4 py-2.5 bg-[#229ED9] text-white text-sm font-semibold hover:bg-[#229ED9]/90 transition-colors">
            <TelegramLogoIcon size={16} weight="fill" /> Telegram
          </button>
        </div>
        <p className="text-[11px] text-muted-foreground text-center">Tip: hit "Copy image", then paste into your X or Telegram post for the full card.</p>
      </div>
    </div>,
    document.body
  );
};
